import {
  SET_PROMOS_SEARCH,
  SET_PROMOS_ISSUE_FILTER,
  UPDATE_PROMOS,
  UPDATE_PROMOTION_DETAIL,
  SET_PROMOTION_DETAIL_LOADING,
  RESET_PROMOTION_DETAIL,
  SET_DISCOUNT_DETAILS_SEARCH,
  SET_PROMOTION_STAGES_LOADING,
  UPDATE_PROMOTION_EVENTS,
  SET_PROMOTION_EVENTS_LOADING,
  SET_PROMOTION_EVENTS_SEARCH,
  SET_PROMOTION_EVENTS_PAGINATION,
  UPDATE_PROMOTION_ISSUES,
  SET_PROMOTION_ISSUES_LOADING,
  UPDATE_PROMOTION_DELIVERIES,
  SET_PROMOTION_DELIVERIES_LOADING,
  OPEN_DISCOUNT_DETAILS_DRAWER,
  CLOSE_DISCOUNT_DETAILS_DRAWER,
  UPDATE_DISCOUNT_DETAILS,
  UPDATE_DISCOUNT_DETAILS_DATA,
  SET_DISCOUNT_DETAILS_LOADING,
  UPDATE_DISCOUNT_ACTIVE_STATUS,
  OPEN_CLIENT_DISCOUNT_DRAWER,
  CLOSE_CLIENT_DISCOUNT_DRAWER,
  SET_LIFECYCLE_FILTER,
  UPDATE_PROMOTION_OWNER,
  UPDATE_PROMOTION_ASSIGNEE,
  REVERT_PROMOTION_ASSIGNEE,
  REVERT_PROMOTION_OWNER,
  OPEN_DELIVERY_DRAWER,
  CLOSE_DELIVERY_DRAWER,
  UPDATE_DELIVERY_NOTES,
  SET_DELIVERY_NOTES_LOADING,
  UPDATE_GRID_PREFERENCES,
  REVERT_GRID_PREFERENCES,
  GRID_PREFERENCES_REQUESTED,
  GRID_PREFERENCES_LOADED,
  GRID_PREFERENCES_FAILED,
} from "./constants";
import { GRID_PREFERENCES_STATUS } from "ppt/constants";
import { fetchGridPreferences } from "ppt/api";

const serializeDate = (date) => {
  if (date instanceof Date) {
    return date.getTime();
  }
  return date ?? null;
};

export const updatePromos = (promos) => {
  if (!Array.isArray(promos)) {
    console.warn(
      "updatePromos: promos should be an Array. Got promos: ",
      promos,
    );
  }

  // Serialize Dates to timestamps
  const serializedPromos = Array.isArray(promos)
    ? promos.map((promo) => ({
        ...promo,
        startDate: serializeDate(promo.startDate),
        endDate: serializeDate(promo.endDate),
      }))
    : [];

  return {
    type: UPDATE_PROMOS,
    payload: serializedPromos,
  };
};

export const setPromosSearch = (search) => {
  return {
    type: SET_PROMOS_SEARCH,
    payload: search,
  };
};

export const setPromosIssueFilter = (value) => ({
  type: SET_PROMOS_ISSUE_FILTER,
  payload: value,
});

export const updatePromotionDetail = (promotionDetail) => {
  if (!promotionDetail) {
    return { type: UPDATE_PROMOTION_DETAIL, payload: null };
  }

  // Serialize Dates to timestamps
  const { stages, ...rest } = promotionDetail;
  const serializedPromotionDetail = {
    ...rest,
    startDate: serializeDate(rest.startDate),
    endDate: serializeDate(rest.endDate),
  };

  return {
    type: UPDATE_PROMOTION_DETAIL,
    payload: {
      promotionDetail: serializedPromotionDetail,
      stages: stages || [],
    },
  };
};

export const updateDiscountDetailsData = (data) => {
  if (!data) {
    return { type: UPDATE_DISCOUNT_DETAILS_DATA, payload: null };
  }

  const serializedDiscounts = data.discounts?.map((discount) => ({
    ...discount,
    startDate: serializeDate(discount.startDate),
    endDate: serializeDate(discount.endDate),
  }));

  return {
    type: UPDATE_DISCOUNT_DETAILS_DATA,
    payload: {
      discounts: serializedDiscounts,
      discountCountByStatus: data.discountCountByStatus,
    },
  };
};

export const setPromotionDetailLoading = (loading) => {
  return {
    type: SET_PROMOTION_DETAIL_LOADING,
    payload: loading,
  };
};

export const setDiscountDetailsSearch = (search) => {
  return {
    type: SET_DISCOUNT_DETAILS_SEARCH,
    payload: search,
  };
};

export const setPromotionStagesLoading = (loading) => {
  return {
    type: SET_PROMOTION_STAGES_LOADING,
    payload: loading,
  };
};

export const updatePromotionEvents = (events) => {
  const serialized = Array.isArray(events)
    ? events.map((event) => ({
        ...event,
        occurredAt: serializeDate(event.occurredAt),
      }))
    : [];
  return {
    type: UPDATE_PROMOTION_EVENTS,
    payload: serialized,
  };
};

export const setPromotionEventsLoading = (loading) => {
  return {
    type: SET_PROMOTION_EVENTS_LOADING,
    payload: loading,
  };
};

export const setPromotionEventsSearch = (search) => {
  return {
    type: SET_PROMOTION_EVENTS_SEARCH,
    payload: search,
  };
};

export const setPromotionEventsPagination = (pagination) => {
  return {
    type: SET_PROMOTION_EVENTS_PAGINATION,
    payload: pagination,
  };
};

export const updatePromotionIssues = (issuesData) => {
  if (!issuesData) {
    return {
      type: UPDATE_PROMOTION_ISSUES,
      payload: null,
    };
  }

  const serializeIssues = (issues) =>
    (issues || []).map((issue) => ({
      ...issue,
      createdAt: serializeDate(issue.createdAt),
    }));

  const serialized = {
    ...issuesData,
    promotionLevel: (issuesData.promotionLevel || []).map((typeGroup) => ({
      ...typeGroup,
      issues: serializeIssues(typeGroup.issues),
    })),
  };

  return {
    type: UPDATE_PROMOTION_ISSUES,
    payload: serialized,
  };
};

export const setPromotionIssuesLoading = (loading) => {
  return {
    type: SET_PROMOTION_ISSUES_LOADING,
    payload: loading,
  };
};

export const updatePromotionDeliveries = (deliveriesData) => {
  return {
    type: UPDATE_PROMOTION_DELIVERIES,
    payload: deliveriesData ?? null,
  };
};

export const setPromotionDeliveriesLoading = (loading) => {
  return {
    type: SET_PROMOTION_DELIVERIES_LOADING,
    payload: loading,
  };
};

export const openDiscountDetailsDrawer = (discountId) => {
  return {
    type: OPEN_DISCOUNT_DETAILS_DRAWER,
    payload: discountId,
  };
};

export const closeDiscountDetailsDrawer = () => {
  return {
    type: CLOSE_DISCOUNT_DETAILS_DRAWER,
  };
};

// Pass the full ClientDiscount row data — drawer renders Details immediately
// without an extra round trip. Audit tab kicks off its own parent fetch on
// open and filters client-side.
export const openClientDiscountDrawer = (clientDiscountRow) => {
  return {
    type: OPEN_CLIENT_DISCOUNT_DRAWER,
    payload: clientDiscountRow,
  };
};

export const closeClientDiscountDrawer = () => {
  return {
    type: CLOSE_CLIENT_DISCOUNT_DRAWER,
  };
};

// One-tile-at-a-time filter set from the lifecycle stat tiles. Pass null to
// clear the filter (the "Active" tile click).
export const setLifecycleFilter = (value) => ({
  type: SET_LIFECYCLE_FILTER,
  payload: value,
});

export const updateDiscountDetails = (detailsData) => {
  if (!detailsData) {
    return {
      type: UPDATE_DISCOUNT_DETAILS,
      payload: null,
    };
  }

  const serializeIssues = (issues) =>
    (issues || []).map((issue) => ({
      ...issue,
      createdAt: serializeDate(issue.createdAt),
    }));

  const serialized = {
    ...detailsData,
    createdAt: serializeDate(detailsData.createdAt),
    updatedAt: serializeDate(detailsData.updatedAt),
    deletedAt: serializeDate(detailsData.deletedAt),
    auditEvents: Array.isArray(detailsData.auditEvents)
      ? detailsData.auditEvents.map((event) => ({
          ...event,
          occurredAt: serializeDate(event.occurredAt),
        }))
      : detailsData.auditEvents,
    issues: detailsData.issues
      ? {
          ...detailsData.issues,
          byType: (detailsData.issues.byType || []).map((type) => ({
            ...type,
            issues: serializeIssues(type.issues),
          })),
        }
      : null,
  };

  return {
    type: UPDATE_DISCOUNT_DETAILS,
    payload: serialized,
  };
};

export const setDiscountDetailsLoading = (loading) => {
  return {
    type: SET_DISCOUNT_DETAILS_LOADING,
    payload: loading,
  };
};

export const resetPromotionDetail = () => {
  return {
    type: RESET_PROMOTION_DETAIL,
  };
};

export const updateDiscountActiveStatusInStore = (updatedDiscount) => {
  return {
    type: UPDATE_DISCOUNT_ACTIVE_STATUS,
    payload: { updatedDiscount },
  };
};

export const updatePromotionOwner = (owner) => ({
  type: UPDATE_PROMOTION_OWNER,
  payload: owner,
});

export const revertPromotionOwner = (owner) => ({
  type: REVERT_PROMOTION_OWNER,
  payload: owner,
});

export const updatePromotionAssignee = (assignee) => ({
  type: UPDATE_PROMOTION_ASSIGNEE,
  payload: assignee,
});

export const revertPromotionAssignee = (assignee) => ({
  type: REVERT_PROMOTION_ASSIGNEE,
  payload: assignee,
});

export const openDeliveryDrawer = (delivery) => {
  return {
    type: OPEN_DELIVERY_DRAWER,
    payload: delivery,
  };
};

export const closeDeliveryDrawer = () => {
  return {
    type: CLOSE_DELIVERY_DRAWER,
  };
};

export const updateDeliveryNotes = (notes) => {
  return {
    type: UPDATE_DELIVERY_NOTES,
    payload: notes,
  };
};

export const setDeliveryNotesLoading = (loading) => {
  return {
    type: SET_DELIVERY_NOTES_LOADING,
    payload: loading,
  };
};

export const updateGridPreferences = (gridColumns) => ({
  type: UPDATE_GRID_PREFERENCES,
  payload: gridColumns,
});

export const revertGridPreferences = (previousGridColumns) => ({
  type: REVERT_GRID_PREFERENCES,
  payload: previousGridColumns,
});

export const gridPreferencesRequested = () => ({
  type: GRID_PREFERENCES_REQUESTED,
});

export const gridPreferencesLoaded = (preferences) => ({
  type: GRID_PREFERENCES_LOADED,
  payload: preferences,
});

export const gridPreferencesFailed = () => ({
  type: GRID_PREFERENCES_FAILED,
});

/**
 * Timeout for the preferences fetch, deliberately far shorter than the api
 * client's 60s default: grids don't render until this settles, so a hung
 * request is a blank page. Eight seconds is long enough for a slow round trip
 * and short enough that falling back to default columns beats waiting.
 */
const GRID_PREFERENCES_TIMEOUT_MS = 8000;

/**
 * Fetches the user's grid preferences, at most once per session.
 *
 * The status check and the status write happen in the same synchronous pass
 * here, before the first await — that's what makes this safe to call from every
 * PPM page mount, a StrictMode double-effect, or two grids racing each other.
 * Whoever arrives second sees LOADING and returns without issuing a request.
 *
 * Never rejects. The outcome comes back as a value so the caller can decide how
 * to surface it, which keeps toast rendering in the component layer where the
 * toast container lives.
 *
 * @returns {Promise<{status: string}>} `alreadyRequested` when another caller
 *   owns the fetch, `loaded` on success, `failed` when the request errored or
 *   timed out (preferences are left empty and grids proceed with defaults)
 */
export const loadGridPreferencesOnce = () => async (dispatch, getState) => {
  const status = getState().ppt?.gridPreferencesStatus;
  if (status !== GRID_PREFERENCES_STATUS.NOT_REQUESTED) {
    return { status: "alreadyRequested" };
  }

  dispatch(gridPreferencesRequested());

  try {
    const preferences = await fetchGridPreferences({
      timeout: GRID_PREFERENCES_TIMEOUT_MS,
    });
    dispatch(gridPreferencesLoaded(preferences));
    return { status: "loaded" };
  } catch (error) {
    console.error("Failed to load grid preferences:", error);
    dispatch(gridPreferencesFailed());
    return { status: "failed" };
  }
};
